import { APP_V2_APP_EVENTS, logError } from "../../lib/analytics";
import { isExpectedTokenExchangeError } from "../tokenExchangeError";

export type CognitoAuthFlow =
  | "magic-link-request"
  | "magic-link-verify"
  | "password"
  | "change-password"
  | "passkey";

/**
 * Walks an error and its nested causes — `cause` then `underlyingError` —
 * calling `visit` at each level. Bounded to 5 hops and cycle-safe; stops early
 * when `visit` returns `true`. Native passkey errors are wrapped several layers
 * deep (Capacitor shim → Amplify), so signals can surface anywhere in the chain.
 */
function walkErrorChain(
  error: unknown,
  visit: (candidate: Record<string, unknown>) => boolean | void
): void {
  const visited = new Set<unknown>();
  let current: unknown = error;

  while (current && typeof current === "object" && !visited.has(current) && visited.size < 5) {
    visited.add(current);
    const candidate = current as Record<string, unknown>;
    if (visit(candidate) === true) {
      return;
    }
    current = candidate.cause ?? candidate.underlyingError;
  }
}

/**
 * User-facing outcomes of a passkey ceremony that we map to a clear message and
 * deliberately do NOT log as unexpected — they are expected user actions or
 * environment conditions, not faults.
 */
const PASSKEY_CEREMONY_RESULTS = {
  alreadyRegistered: {
    code: "auth/passkey-already-registered",
    message: "A passkey is already registered on this device for your account.",
  },
  cancelled: {
    code: "auth/passkey-cancelled",
    message: "Passkey prompt was dismissed or timed out. Please try again.",
  },
  securityError: {
    code: "auth/passkey-security-error",
    message:
      "Passkeys can't be used on this page. Please make sure you are on a supported, secure domain.",
  },
  notSupported: {
    code: "auth/passkey-not-supported",
    message: "Passkeys are not supported on this device or browser.",
  },
} as const;

/**
 * Classifies a passkey ceremony error into a known, user-facing outcome, or
 * returns `undefined` for genuine failures that should fall through to the
 * default handler and be logged.
 *
 * Amplify ALWAYS wraps the raw WebAuthn `DOMException` in a `PasskeyError` whose
 * top-level `name` is an Amplify `PasskeyErrorCode` (e.g. `NotAllowedError` →
 * `PasskeyRegistrationCanceled`/`PasskeyAuthenticationCanceled`,
 * `InvalidStateError` → `PasskeyAlreadyExists`, `SecurityError` →
 * `RelyingPartyMismatch`, `AbortError` → `PasskeyOperationAborted`), with the
 * original DOMException on `underlyingError`. Capacitor's native shim surfaces
 * the same condition on a `code` instead. So the signal can sit on the
 * top-level name/code, a nested cause/`underlyingError`, or only the message —
 * walk the chain and match BOTH the raw DOMException names and Amplify's codes.
 *
 * Intentionally NOT classified here (left to fall through and be logged):
 * `PasskeyRegistrationFailed`, `PasskeyRetrievalFailed`,
 * `InvalidPasskeyRegistrationOptions`, `InvalidPasskeyAuthenticationOptions`,
 * and the generic `Unknown` wrapper — these are real failures/misconfigurations
 * worth surfacing in analytics.
 */
function classifyPasskeyCeremonyError(
  error: unknown
): { code: string; message: string } | undefined {
  let alreadyRegistered = false;
  let cancelled = false;
  let securityError = false;
  let notSupported = false;

  walkErrorChain(error, (candidate) => {
    const name = typeof candidate.name === "string" ? candidate.name : "";
    const code = typeof candidate.code === "string" ? candidate.code : "";
    const message = typeof candidate.message === "string" ? candidate.message : "";
    // The discriminating value is the DOMException name on web/Amplify and the
    // error code on Capacitor's native shim; treat them interchangeably.
    const signal = name || code;

    if (
      signal === "InvalidStateError" ||
      signal === "PasskeyAlreadyExists" ||
      message.includes("excluded credentials")
    ) {
      alreadyRegistered = true;
    }
    if (
      signal === "NotAllowedError" ||
      signal === "AbortError" ||
      signal === "PasskeyRegistrationCanceled" ||
      signal === "PasskeyAuthenticationCanceled" ||
      signal === "PasskeyOperationAborted"
    ) {
      cancelled = true;
    }
    if (signal === "SecurityError" || signal === "RelyingPartyMismatch") {
      securityError = true;
    }
    if (signal === "NotSupportedError" || signal === "PasskeyNotSupported") {
      notSupported = true;
    }
  });

  // "Already registered" is the most specific signal, so it wins ties.
  if (alreadyRegistered) {
    return PASSKEY_CEREMONY_RESULTS.alreadyRegistered;
  }
  if (cancelled) {
    return PASSKEY_CEREMONY_RESULTS.cancelled;
  }
  if (securityError) {
    return PASSKEY_CEREMONY_RESULTS.securityError;
  }
  if (notSupported) {
    return PASSKEY_CEREMONY_RESULTS.notSupported;
  }
  return undefined;
}

/**
 * Builds a serializable snapshot of an error and its nested causes for
 * diagnostics. Error objects (and DOMExceptions) don't serialize their useful
 * fields by default, and passkey errors are wrapped several layers deep, so
 * this captures name/code/message/constructor and own keys at each level. Used
 * to debug unexpected errors — e.g. to confirm where the WebAuthn
 * `InvalidStateError` signal actually lands when a passkey flow fails.
 */
function describeErrorChain(error: unknown): Array<Record<string, unknown>> {
  const chain: Array<Record<string, unknown>> = [];
  walkErrorChain(error, (candidate) => {
    chain.push({
      constructor: (candidate.constructor as { name?: string } | undefined)?.name,
      name: candidate.name,
      code: candidate.code,
      message: candidate.message,
      ownKeys: Object.getOwnPropertyNames(candidate),
    });
  });
  return chain;
}

/**
 * Computes the diagnostic error chain for an unmapped error and, for the passkey
 * flow, prints it to the device log (logcat / browser console) so the wrapped
 * shape can be inspected on-device. Returns the chain for inclusion in analytics.
 */
function diagnoseUnmappedError(
  error: unknown,
  flow: CognitoAuthFlow
): Array<Record<string, unknown>> {
  const errorChain = describeErrorChain(error);
  if (flow === "passkey") {
    // eslint-disable-next-line no-console -- intentional on-device diagnostic
    console.error("[passkey] unmapped auth error chain:", JSON.stringify(errorChain));
  }
  return errorChain;
}

export function mapCognitoError(
  error: unknown,
  flow: CognitoAuthFlow = "magic-link-verify"
): { code: string; message: string } {
  if (isExpectedTokenExchangeError(error)) {
    return {
      code: "auth/token-exchange-forbidden",
      message: "Sign in could not be completed. Please try again.",
    };
  }

  // Passkey ceremonies: Amplify wraps the raw WebAuthn DOMException in a
  // PasskeyError, so the top-level `name` won't match the DOMException cases in
  // the switch below — classify by walking the chain. Known user-facing
  // outcomes (cancelled/timed-out, already-registered, domain mismatch,
  // unsupported) get a clear message and are NOT logged as unexpected; genuine
  // failures return undefined and fall through to the logged default handler.
  if (flow === "passkey") {
    const passkeyResult = classifyPasskeyCeremonyError(error);
    if (passkeyResult) {
      return passkeyResult;
    }
  }

  if (error && typeof error === "object" && "name" in error) {
    const amplifyError = error as { name: string; message: string };

    switch (amplifyError.name) {
      // When the DefineAuthChallenge Lambda throws an error (instead of returning
      // failAuthentication: true), Cognito surfaces it as a UserLambdaValidationException
      // with the Lambda's error message embedded. We use this to distinguish "user type
      // not allowed" from generic auth failures.
      case "UserLambdaValidationException": {
        if (amplifyError.message.includes("USER_TYPE_NOT_ALLOWED")) {
          return {
            code: "auth/user-type-not-allowed",
            message:
              "Your account type does not have access to this application. Please make sure you are using the correct app for your account.",
          };
        }
        if (amplifyError.message.includes("Unsupported custom challenge sub-type")) {
          return {
            code: "auth/token-exchange-forbidden",
            message: "Sign in could not be completed. Please try again.",
          };
        }
        logError(APP_V2_APP_EVENTS.COGNITO_AUTH_UNEXPECTED_ERROR, {
          error,
          metadata: { errorName: amplifyError.name, errorMessage: amplifyError.message },
        });
        return {
          code: "auth/lambda-error",
          message: "An authentication error occurred.",
        };
      }
      case "UserNotFoundException": {
        return {
          code: "auth/user-not-found",
          message: "No registered user found for the provided email address.",
        };
      }
      // Cognito returns NotAuthorizedException with "Incorrect username or password" for:
      // - Magic-link request flow: user does not exist (CUSTOM_AUTH obscures existence)
      // - Magic-link verify flow: challenge response failed → expired/invalid link
      // - Password flow: wrong credentials entered by user
      case "NotAuthorizedException": {
        if (amplifyError.message.includes("Incorrect username or password")) {
          if (flow === "change-password") {
            return {
              code: "auth/incorrect-password",
              message: "Current password is incorrect. Please try again.",
            };
          }
          if (flow === "password") {
            return {
              code: "auth/incorrect-credentials",
              message: "Incorrect email or password. Please try again.",
            };
          }
          if (flow === "magic-link-request") {
            return {
              code: "auth/user-not-found",
              message: "No registered user found for the provided email address.",
            };
          }
          return {
            code: "auth/invalid-link",
            message:
              "This sign-in link has expired or has already been used. Links are valid for 10 minutes. Please request a new one.",
          };
        }
        if (flow === "change-password") {
          return {
            code: "auth/re-auth-required",
            message: "Your session has expired. Please sign in again and retry.",
          };
        }
        return {
          code: "auth/user-not-found",
          message: "No registered user found for the provided email address.",
        };
      }
      case "TooManyRequestsException": {
        return {
          code: "auth/too-many-requests",
          message: "Too many requests, please try again later",
        };
      }
      case "LimitExceededException": {
        return {
          code: "auth/limit-exceeded",
          message: "Attempt limit exceeded, please try after some time",
        };
      }
      case "CodeMismatchException": {
        return {
          code: "auth/invalid-code",
          message: "The reset code is invalid. Please request a new one.",
        };
      }
      case "ExpiredCodeException": {
        return {
          code: "auth/expired-code",
          message: "The reset code has expired. Please request a new one.",
        };
      }
      case "PasswordResetRequiredException": {
        if (flow === "password") {
          return {
            code: "auth/password-reset-required",
            message:
              'Your password needs to be reset. Please click "Forgot password?" below to set a new password.',
          };
        }
        return {
          code: "auth/password-reset-required",
          message:
            "Your password needs to be reset before you can sign in. Please go back to the login page and use the password login option to reset your password.",
        };
      }
      case "InvalidPasswordException": {
        return {
          code: "auth/invalid-password",
          message:
            "Password does not meet requirements. It must be at least 12 characters and include uppercase and lowercase letters.",
        };
      }
      case "NetworkError": {
        return { code: "auth/network-request-failed", message: "Network error occurred" };
      }
      // Browser WebAuthn ceremony errors (passkey flow). These surface as
      // DOMExceptions from navigator.credentials.{get,create}.
      case "NotAllowedError":
      case "AbortError": {
        return {
          code: "auth/passkey-cancelled",
          message: "Passkey prompt was dismissed or timed out. Please try again.",
        };
      }
      case "InvalidStateError": {
        return {
          code: "auth/passkey-already-registered",
          message: "A passkey is already registered on this device for your account.",
        };
      }
      case "SecurityError": {
        return {
          code: "auth/passkey-security-error",
          message:
            "Passkeys can't be used on this page. Please make sure you are on a supported, secure domain.",
        };
      }
      case "NotSupportedError": {
        return {
          code: "auth/passkey-not-supported",
          message: "Passkeys are not supported on this device or browser.",
        };
      }
      default: {
        const errorChain = diagnoseUnmappedError(error, flow);
        logError(APP_V2_APP_EVENTS.COGNITO_AUTH_UNEXPECTED_ERROR, {
          error,
          metadata: {
            flow,
            errorName: amplifyError.name,
            errorMessage: amplifyError.message,
            errorChain,
          },
        });
        return {
          code: "auth/unknown",
          message: amplifyError.message || "An unknown error occurred",
        };
      }
    }
  }

  const errorChain = diagnoseUnmappedError(error, flow);
  logError(APP_V2_APP_EVENTS.COGNITO_AUTH_UNEXPECTED_ERROR, {
    error: error instanceof Error ? error : new Error(String(error)),
    metadata: { flow, errorMessage: String(error), errorChain },
  });
  return { code: "auth/unknown", message: "An unknown error occurred" };
}
